import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../components/Icon.jsx';
import Modal from '../../components/Modal.jsx';
import { useT, useLang } from '../../lib/i18n.jsx';
import { get, post, del } from '../../lib/api.js';
import { useAsync } from '../../lib/hooks.js';
import { toast } from '../../lib/toast.jsx';
import { errorMessage } from '../../lib/errors.js';
import { formatDate, formatMoney, formatMonthly, parseApiDate } from '../../lib/format.js';
import { invoiceStatusClass, invoiceStatusLabel, isOpenInvoice } from '../../lib/status.js';

export default function BillingPage() {
  const t = useT();
  const { lang } = useLang();
  const navigate = useNavigate();

  const { data: invoices, loading, error } = useAsync(() => get('/invoices'), []);
  const { data: hostings } = useAsync(() => get('/hostings').catch(() => []), []);
  // GET /payments/mandate returns null when there is none. Any failure is a
  // real error and must not be shown as "no payment method".
  const {
    data: mandate,
    loading: mandateLoading,
    error: mandateError,
    reload: reloadMandate,
  } = useAsync(
    () =>
      get('/payments/mandate').catch((e) => {
        if (e?.status === 404) return null;
        throw e;
      }),
    [],
  );
  const [revokeOpen, setRevokeOpen] = useState(false);

  const list = invoices || [];
  const openInvoices = list.filter(isOpenInvoice);
  const totalOpen = openInvoices.reduce((a, i) => a + (i.total_amount || 0), 0);
  const now = new Date();
  const thisMonth = list
    .filter((i) => {
      const d = parseApiDate(i.issue_date);
      return d && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear() && i.status !== 'cancelled';
    })
    .reduce((a, i) => a + (i.total_amount || 0), 0);

  // Next invoice: the earliest upcoming billing date over running hostings.
  const billable = (hostings || []).filter((h) => h.status === 'active' && h.next_billing_date);
  const nextDate = billable
    .map((h) => parseApiDate(h.next_billing_date))
    .filter(Boolean)
    .sort((a, b) => a - b)[0];
  const nextAmount = nextDate
    ? billable
        .filter((h) => parseApiDate(h.next_billing_date)?.toDateString() === nextDate.toDateString())
        .reduce((a, h) => a + (h.package?.price_monthly || 0), 0)
    : 0;

  const setupMandate = async () => {
    try {
      const res = await post('/payments/mandate/setup', { redirect_url: window.location.href });
      if (res?.checkout_url) window.location.href = res.checkout_url;
      else toast(t('billingPage.noPaymentLink'));
    } catch (e) {
      toast(errorMessage(e, { lang }));
    }
  };

  const mandateValue = mandateLoading ? '…' : mandateError ? '!' : mandate ? 'SEPA' : '—';
  const mandateSub = mandateLoading
    ? ''
    : mandateError
      ? t('billingPage.mandate.loadFailed')
      : mandate
        ? mandate.iban_masked || mandate.consumer_name || ''
        : t('billingPage.mandate.none');

  return (
    <main className="page" data-screen-label="Billing">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('bl.title')}</h1>
          <p className="page-sub">{t('bl.sub')}</p>
        </div>
        <div className="actions">
          {totalOpen > 0 && openInvoices[0] && (
            <button className="btn-primary" onClick={() => navigate(`/facturatie/${openInvoices[0].id}`)}>
              <Icon name="card" size={15} stroke={2} />
              <span style={{ marginLeft: 6 }}>{t('bl.pay')} · {formatMoney(totalOpen, lang)}</span>
            </button>
          )}
        </div>
      </div>

      <div className="kpi-grid kpi-grid--4">
        <Kpi
          label={t('bl.kpi.open')}
          value={formatMoney(totalOpen, lang)}
          sub={totalOpen > 0
            ? t(openInvoices.length === 1 ? 'billingPage.openCount.one' : 'billingPage.openCount.other', { n: openInvoices.length })
            : t('billingPage.allPaid')}
          icon="alert"
          tone={totalOpen > 0 ? 'warn' : null}
        />
        <Kpi label={t('bl.kpi.this_month')} value={formatMoney(thisMonth, lang)} sub={t('billingPage.inclVat')} icon="card" />
        <Kpi
          label={t('bl.kpi.next')}
          value={nextDate ? formatDate(nextDate, lang) : '—'}
          sub={nextDate ? t('billingPage.next.approx', { amount: formatMoney(nextAmount, lang) }) : t('billingPage.next.none')}
          icon="rotate"
        />
        <Kpi label={t('bl.kpi.method')} value={mandateValue} sub={mandateSub} icon="shield" tone={mandateError ? 'warn' : null} />
      </div>

      {!mandateLoading && !mandateError && (
        <div className="banner">
          <div>
            <h2>{mandate ? t('billingPage.sepa.enabled') : t('bl.banner.title')}</h2>
            <p>
              {mandate ? t('billingPage.sepa.body') : t('bl.banner.body')}
            </p>
          </div>
          {mandate ? (
            <button className="btn-ghost" style={{ padding: '8px 14px' }} onClick={() => setRevokeOpen(true)}>
              {t('billingPage.revoke.cta')} →
            </button>
          ) : (
            <button className="btn-ghost" style={{ padding: '8px 14px' }} onClick={setupMandate}>
              {t('bl.banner.cta')} →
            </button>
          )}
        </div>
      )}

      <div className="dash-grid">
        <section className="card" style={{ padding: 0 }}>
          <div className="card-h">
            <h3>{t('bl.invoices.title')}</h3>
          </div>
          <div className="bl-head">
            <span>{t('bl.col.number')}</span>
            <span>{t('bl.col.date')}</span>
            <span>{t('bl.col.amount')}</span>
            <span>{t('bl.col.due')}</span>
            <span>{t('bl.col.status')}</span>
            <span />
          </div>
          {loading && <div style={{ padding: '14px 22px', color: 'var(--ink-3)' }}>{t('common.loading')}</div>}
          {!loading && error && (
            <div style={{ padding: '24px 22px', textAlign: 'center', color: 'var(--danger)' }} role="alert">
              {errorMessage(error, { lang })}
            </div>
          )}
          {!loading && !error && list.length === 0 && (
            <div style={{ padding: '24px 22px', textAlign: 'center', color: 'var(--ink-3)' }}>
              {t('billingPage.noInvoices')}
            </div>
          )}
          {list.map((inv) => (
            <div
              key={inv.id}
              className="bl-row"
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/facturatie/${inv.id}`)}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/facturatie/${inv.id}`); }}
            >
              <div className="mono" style={{ fontSize: 13, color: 'var(--ink)' }}>{inv.invoice_number}</div>
              <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>{formatDate(inv.issue_date, lang)}</div>
              <div className="mono" style={{ fontSize: 13, color: 'var(--ink-2)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                {formatMoney(inv.total_amount, lang)}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--ink-3)', textAlign: 'right' }}>{formatDate(inv.due_date, lang)}</div>
              <div style={{ textAlign: 'right' }}>
                <span className={'status-pill ' + invoiceStatusClass(inv)}>
                  <span className="dot" />
                  {invoiceStatusLabel(inv, lang)}
                </span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <Icon name="arrow-right" size={14} />
              </div>
            </div>
          ))}
        </section>

        <aside className="side-stack">
          <section className="card">
            <div className="card-h">
              <h3>{t('bl.subs.title')}</h3>
            </div>
            <div className="subs-list">
              {(hostings || []).map((h) => (
                <div className="sub-row" key={h.id}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: 'var(--ink)', letterSpacing: '-0.005em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {h.package?.name || t('nav.hosting')} · {h.domain}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--ink-4)', marginTop: 2 }}>
                      {formatMonthly(h.package?.price_monthly || 0, lang)} ·{' '}
                      {t('billingPage.renews', { date: h.next_billing_date ? formatDate(h.next_billing_date, lang) : '—' })}
                    </div>
                  </div>
                </div>
              ))}
              {(hostings || []).length === 0 && (
                <div style={{ padding: '14px 22px', color: 'var(--ink-3)', fontSize: 13 }}>
                  {t('billingPage.noSubscriptions')}
                </div>
              )}
            </div>
          </section>

          <section className="card">
            <div className="card-h">
              <h3>{t('bl.kpi.method')}</h3>
              {!mandateLoading && !mandateError && !mandate && (
                <button className="more" onClick={setupMandate}>+ {t('billingPage.add')}</button>
              )}
            </div>
            <div className="method-list">
              {mandateLoading ? (
                <div style={{ padding: '14px 22px', color: 'var(--ink-3)', fontSize: 13 }}>{t('common.loading')}</div>
              ) : mandateError ? (
                <div style={{ padding: '14px 22px', fontSize: 13 }} role="alert">
                  <div style={{ color: 'var(--danger)' }}>
                    {t('billingPage.method.loadFailed')}
                  </div>
                  <div style={{ color: 'var(--ink-3)', marginTop: 4 }}>{errorMessage(mandateError, { lang })}</div>
                  <button className="btn-ghost" style={{ marginTop: 10 }} onClick={reloadMandate}>
                    {t('billingPage.retry')}
                  </button>
                </div>
              ) : mandate ? (
                <div className="method-row is-primary">
                  <span className="method-row__icon is-sepa">
                    <Icon name="card" size={14} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="mono" style={{ fontSize: 13, color: 'var(--ink)' }}>SEPA · {mandate.iban_masked || '—'}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--ink-4)', marginTop: 2 }}>
                      {[mandate.consumer_name, mandateStatusLabel(mandate.status, t)].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '14px 22px', color: 'var(--ink-3)', fontSize: 13 }}>
                  {t('billingPage.method.none')}
                </div>
              )}
            </div>
          </section>
        </aside>
      </div>

      {revokeOpen && mandate && (
        <RevokeMandateDialog
          lang={lang}
          mandate={mandate}
          onClose={() => setRevokeOpen(false)}
          onDone={() => {
            setRevokeOpen(false);
            reloadMandate();
          }}
        />
      )}
    </main>
  );
}

const MANDATE_STATUSES = ['valid', 'pending', 'invalid', 'revoked'];

function mandateStatusLabel(s, t) {
  return MANDATE_STATUSES.includes(s) ? t(`billingPage.mandateStatus.${s}`) : s || '';
}

function RevokeMandateDialog({ lang, mandate, onClose, onDone }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const revoke = async () => {
    setBusy(true);
    setErr('');
    try {
      await del(`/payments/mandate/${mandate.id}`);
      toast(t('billingPage.revoke.done'));
      onDone();
    } catch (e) {
      setErr(errorMessage(e, { lang }));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      onClose={onClose}
      className="modal--danger"
      maxWidth={460}
      title={t('billingPage.revoke.title')}
      description={t('billingPage.revoke.body')}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            {t('g.cancel')}
          </button>
          <button type="button" className="btn-danger" disabled={busy} onClick={revoke}>
            {busy ? t('billingPage.working') : t('billingPage.revoke.confirm')}
          </button>
        </>
      }
    >
      {err && (
        <div className="modal-body">
          <div className="login-error" role="alert">{err}</div>
        </div>
      )}
    </Modal>
  );
}

function Kpi({ label, value, sub, icon, tone }) {
  return (
    <div className="kpi-card">
      <div className="kpi-card__top">
        <span className={`kpi-card__icon ${tone === 'warn' ? 'is-warn' : ''}`}>
          <Icon name={icon} size={16} />
        </span>
        <span className="kpi-card__label">{label}</span>
      </div>
      <div className="kpi-card__value" style={{ fontSize: 22 }}>{value}</div>
      <div className="kpi-card__sub">{sub}</div>
    </div>
  );
}
